import { useRef, useState, type FormEvent, type MouseEvent } from 'react';
import { Confirmar } from './Confirmar.tsx';
import { GuardaDoFormulario } from './GuardaDoFormulario.tsx';
import { Icone } from './Icone.tsx';
import { CampoDoPasso, JanelaEmBlocos } from './PassoAPasso.tsx';
import { usarFormulario } from '../lib/formulario.ts';
import { usarDialogo } from '../lib/dialogo.ts';
import { FORMATOS_DO_POST, REDES_DO_POST } from '../lib/posts.ts';
import type { DadosDoPost, PostDeMarketing, RedeDoPost } from '../lib/api.ts';

/**
 * ⭐⭐ NOVO POST e EDITAR POST — o calendário editorial, 15/10/2026 (PRD §50).
 *
 * O desenho é a janela em blocos da casa (`JanelaEmBlocos`), com as mesmas duas
 * guardas de todo formulário: confirmar ao salvar e avisar ao fechar com
 * alteração pendente.
 *
 * ⚠️ **A situação NÃO é campo do formulário.** É a separação do funil
 * (*"editar cadastro e mover o funil são coisas diferentes"*): corrigir um tema
 * não é publicar, e publicar tem regra própria (a data em que foi ao ar). O
 * post novo nasce como ideia.
 *
 * ⭐ **Desde a parte 2 (PRD §50.5) a escada mora na FICHA do post**
 * (`EscadaDoPost`), em página própria, e esta janela é só o plano: tema, data,
 * redes, formato e observação.
 */

function doPost(p: PostDeMarketing | null, dataSugerida: string): DadosDoPost {
  return {
    titulo: p?.titulo ?? '',
    redes: p?.redes ?? ['instagram', 'linkedin'],
    formato: p?.formato ?? 'carrossel',
    dataPrevista: p?.dataPrevista ?? dataSugerida,
    observacao: p?.observacao ?? '',
  };
}

export function FormularioPost({
  inicial,
  dataSugerida,
  aoFechar,
  aoSalvar,
  aoApagar,
}: {
  inicial: PostDeMarketing | null;
  /** O dia em que se clicou na grade, ou hoje. */
  dataSugerida: string;
  aoFechar: () => void;
  aoSalvar: (dados: DadosDoPost) => Promise<void>;
  aoApagar: () => Promise<void>;
}) {
  const editando = inicial !== null;
  const [d, setD] = useState<DadosDoPost>(() => doPost(inicial, dataSugerida));
  const [erro, setErro] = useState<string | null>(null);
  const [salvando, setSalvando] = useState(false);
  const [apagando, setApagando] = useState(false);
  const emVoo = useRef(false);

  const guarda = usarFormulario(d, aoFechar);
  /* ⚠️ Com outra janela por cima, esta não escuta o Esc (a lição da gaveta do
     funil, 20/09/2026): o Esc fecha só a de cima. */
  usarDialogo(guarda.fechar, !apagando && !guarda.pendencia);

  const muda = <K extends keyof DadosDoPost>(k: K, v: DadosDoPost[K]) => {
    setErro(null);
    setD((x) => ({ ...x, [k]: v }));
  };

  function alternarRede(r: RedeDoPost) {
    const tem = d.redes.includes(r);
    /* ⛔ Post sem rede não sai: a última marcada não desmarca. */
    if (tem && d.redes.length === 1) return setErro('O post sai em ao menos uma rede.');
    muda('redes', REDES_DO_POST.map((x) => x.id).filter((x) => (x === r ? !tem : d.redes.includes(x))));
  }

  function enviar(evento: FormEvent | MouseEvent) {
    evento.preventDefault();
    if (emVoo.current) return;
    if (!d.titulo.trim()) return setErro('Escreva o tema do post.');
    if (!d.dataPrevista) return setErro('Diga a data prevista do post.');
    guarda.pedirParaSalvar();
  }

  async function gravar() {
    if (emVoo.current) return;
    guarda.cancelarPendencia();
    emVoo.current = true;
    setSalvando(true);
    setErro(null);
    try {
      await aoSalvar({ ...d, titulo: d.titulo.trim(), observacao: d.observacao.trim() });
    } catch (e) {
      setErro(e instanceof Error ? e.message : 'Não consegui salvar.');
      emVoo.current = false;
      setSalvando(false);
    }
  }

  return (
    <>
      <JanelaEmBlocos
        rotulo={editando ? 'Editar post' : 'Novo post'}
        sobrelinha={editando ? 'Marketing · editar post' : 'Marketing · novo post'}
        titulo={editando ? 'Editar post' : 'Novo post no calendário'}
        sub={
          editando
            ? 'O plano do post. A situação e o conteúdo ficam na ficha.'
            : 'Nasce como ideia. Pede o tema, a data e ao menos uma rede.'
        }
        largura="media"
        aoFechar={guarda.fechar}
        erro={erro}
        rodape={
          <>
            {editando && (
              <button type="button" className="imv-bt fantasma perigo" onClick={() => setApagando(true)}>
                <Icone nome="lixeira" tamanho={14} /> Apagar
              </button>
            )}
            <span className="imv-esp" />
            <button type="button" className="imv-bt fantasma" onClick={guarda.fechar}>
              Cancelar
            </button>
            <button type="button" className="imv-bt primario" disabled={salvando} onClick={(e) => void enviar(e)}>
              <Icone nome="check" tamanho={14} /> {salvando ? 'Salvando…' : 'Salvar'}
            </button>
          </>
        }
      >
        <div className="imv-grade-campos">
          <CampoDoPasso rotulo="Tema" obrigatorio col={6} nota="A frase do post, como ele vai dizer.">
            <input
              value={d.titulo}
              onChange={(e) => muda('titulo', e.target.value)}
              maxLength={300}
              autoFocus={!editando}
            />
          </CampoDoPasso>

          <CampoDoPasso rotulo="Data prevista" obrigatorio col={2} mono>
            <input type="date" value={d.dataPrevista} onChange={(e) => muda('dataPrevista', e.target.value)} />
          </CampoDoPasso>

          {/* As redes são escolha múltipla: o mesmo post sai nas duas. */}
          <div className="imv-campo c4">
            <span className="imv-rotulo">
              Redes
              <i className="imv-req" title="obrigatório" />
            </span>
            <div className="imv-seg" role="group" aria-label="Redes">
              {REDES_DO_POST.map((r) => (
                <button
                  key={r.id}
                  type="button"
                  aria-pressed={d.redes.includes(r.id)}
                  className={`imv-op mkt-rede${d.redes.includes(r.id) ? ' on' : ''}`}
                  onClick={() => alternarRede(r.id)}
                >
                  <Icone nome={r.id} tamanho={15} />
                  {r.nome}
                </button>
              ))}
            </div>
          </div>

          <div className="imv-campo c6">
            <span className="imv-rotulo">Formato</span>
            <div className="imv-seg" role="radiogroup" aria-label="Formato">
              {FORMATOS_DO_POST.map((f) => (
                <button
                  key={f.id}
                  type="button"
                  role="radio"
                  aria-checked={d.formato === f.id}
                  className={`imv-op${d.formato === f.id ? ' on' : ''}`}
                  onClick={() => muda('formato', f.id)}
                >
                  <i />
                  {f.nome}
                </button>
              ))}
            </div>
            {/* ⚠️ Avisa e não bloqueia: o Instagram pode mudar, e quem decide é quem publica. */}
            {d.formato === 'texto' && d.redes.includes('instagram') && (
              <small className="mkt-aviso">O Instagram não publica post só de texto.</small>
            )}
          </div>

          <CampoDoPasso rotulo="Observação" col={6} nota="Para quem vai escrever ou publicar.">
            <textarea rows={3} value={d.observacao} onChange={(e) => muda('observacao', e.target.value)} maxLength={4000} />
          </CampoDoPasso>
        </div>
      </JanelaEmBlocos>

      <GuardaDoFormulario
        pendencia={guarda.pendencia}
        oQue="o post"
        aoCancelar={guarda.cancelarPendencia}
        aoSalvar={() => void gravar()}
        aoDescartar={guarda.aoFecharDeVerdade}
      />

      {apagando && (
        <Confirmar
          titulo="Apagar o post"
          descricao={`"${inicial?.titulo ?? d.titulo}" sai do calendário, com o conteúdo dele: os slides, a legenda, os textos, as imagens dos fundos e as artes montadas. Isso não se desfaz.`}
          aoFechar={() => setApagando(false)}
          aoConfirmar={() => {
            setApagando(false);
            void aoApagar().catch((e) => setErro(e instanceof Error ? e.message : 'Não consegui apagar.'));
          }}
        />
      )}
    </>
  );
}
